import { useEffect, useState } from "react";
import { dayKey, streakFrom } from "@/lib/srs";
import { k, onExternalChange, readJSON, writeJSON } from "@/lib/storage";
import { getDeviceId } from "@/lib/syncState";
import {
  activityDayKeys,
  activityKey,
  foldActivity,
  sumDay,
  type ActivityMap,
  type DayActivity,
  type RawActivityMap,
} from "@/lib/activity";

export type { ActivityMap, DayActivity, RawActivityMap };

/**
 * Daily study counters, stored one bucket per device.
 *
 * A counter is the one kind of state last-write-wins cannot merge — ten
 * reviews here and five there have to add up, not overwrite. Giving each
 * device its own `YYYY-MM-DD#<deviceId>` row means no two devices ever write
 * the same record, so there is no conflict to resolve; the buckets are summed
 * on read. See `lib/activity.ts` for the folding.
 */
const KEY = k("activity.v2");

const listeners = new Set<() => void>();
let snapshot: RawActivityMap = readJSON<RawActivityMap>(KEY, {});

function commit(next: RawActivityMap): void {
  snapshot = next;
  writeJSON(KEY, next);
  for (const fn of listeners) fn();
}

// Two tabs share one device bucket, so without this the second tab's counters
// would overwrite the first's instead of continuing them.
onExternalChange(KEY, () => {
  snapshot = readJSON<RawActivityMap>(KEY, {});
  for (const fn of listeners) fn();
});

/** This device's bucket for the day `now` falls in. */
export function todayKey(now = Date.now()): string {
  return activityKey(dayKey(new Date(now)), getDeviceId());
}

/**
 * Record one answer against today. Called for drill answers, flashcard
 * verdicts, review ratings and manual mastery marks — everything that counts
 * as studying.
 */
export function logStudy(opts: { learned: boolean }, now = Date.now()): void {
  const key = todayKey(now);
  const prev = snapshot[key] ?? { reviews: 0, learned: 0 };
  commit({
    ...snapshot,
    [key]: {
      reviews: prev.reviews + 1,
      learned: prev.learned + (opts.learned ? 1 : 0),
    },
  });
}

export function todayActivity(now = Date.now()): DayActivity {
  return sumDay(snapshot, dayKey(new Date(now)));
}

export function currentStreak(now = Date.now()): number {
  return streakFrom(activityDayKeys(snapshot), new Date(now));
}

/**
 * Today's counts plus the running streak, re-rendering on every logged answer.
 */
export function useActivity(): {
  today: DayActivity;
  streak: number;
  days: ActivityMap;
} {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  return { today: todayActivity(), streak: currentStreak(), days: foldActivity(snapshot) };
}

/**
 * The raw per-device buckets. Sync and the progress export both need these
 * rather than the folded totals — folding is lossy, and a restored export has
 * to keep each device's contribution separate to stay mergeable.
 */
export function allActivity(): RawActivityMap {
  return snapshot;
}

export function replaceActivity(next: RawActivityMap): void {
  commit(next);
}

export function resetActivity(): void {
  commit({});
}
